// Leaderboard data: the game_leaderboard / game_my_stats RPCs
// (supabase/migrations/20261012_game_leaderboards.sql). Results are written
// only by the `games` edge function when a table finishes a game.

import { useEffect, useState } from 'react';
import { supabase } from '../../config/supabase';

export type LbRange = 'week' | 'month' | 'all';
export type LbOrder = 'wins' | 'points';

export interface LbRow {
  rank: number;
  user_id: string;
  name: string;
  games: number;
  wins: number;
  losses: number;
  draws: number;
  points: number;
  best_points: number | null;
  is_me: boolean;
}

export interface MyStats {
  kind: string;
  games: number;
  wins: number;
  losses: number;
  draws: number;
  points: number;
  best_points: number | null;
  last_played: string | null;
}

export const RANGE_LABELS: Record<LbRange, string> = { week: 'This week', month: 'This month', all: 'All time' };

const num = (v: unknown) => Number(v ?? 0);

export async function fetchLeaderboard(opts: { kind: string; worldId?: string | null; range?: LbRange; order?: LbOrder; limit?: number }): Promise<LbRow[]> {
  const { data, error } = await supabase.rpc('game_leaderboard', {
    p_kind: opts.kind, p_world: opts.worldId ?? null, p_range: opts.range ?? 'all', p_order: opts.order ?? 'wins', p_limit: opts.limit ?? 20,
  });
  if (error) throw error;
  return ((data || []) as any[]).map((r) => ({
    rank: num(r.rank), user_id: r.user_id, name: r.name || 'Player', games: num(r.games), wins: num(r.wins), losses: num(r.losses),
    draws: num(r.draws), points: num(r.points), best_points: r.best_points ?? null, is_me: !!r.is_me,
  }));
}

/** The signed-in player's record per game ([] when signed out). */
export async function fetchMyStats(kind?: string, range: LbRange = 'all'): Promise<MyStats[]> {
  const { data: s } = await supabase.auth.getSession();
  if (!s.session) return [];
  const { data, error } = await supabase.rpc('game_my_stats', { p_kind: kind ?? null, p_range: range });
  if (error) throw error;
  return ((data || []) as any[]).map((r) => ({
    kind: r.kind, games: num(r.games), wins: num(r.wins), losses: num(r.losses), draws: num(r.draws),
    points: num(r.points), best_points: r.best_points ?? null, last_played: r.last_played ?? null,
  }));
}

/** Leaderboard rows for a view; refetches when any argument (or refreshKey) changes. */
export function useLeaderboard(kind: string, worldId: string | null, range: LbRange, order: LbOrder, limit = 20, refreshKey: unknown = 0) {
  const [state, set] = useState<{ rows: LbRow[]; loading: boolean; error: string | null }>({ rows: [], loading: true, error: null });
  useEffect(() => {
    let live = true;
    set((s) => ({ ...s, loading: true, error: null }));
    // A finished game's rows land with the table's final write; give it a beat.
    const t = window.setTimeout(() => {
      fetchLeaderboard({ kind, worldId, range, order, limit })
        .then((rows) => { if (live) set({ rows, loading: false, error: null }); })
        .catch((e) => { if (live) set({ rows: [], loading: false, error: e?.message || 'Could not load the leaderboard' }); });
    }, refreshKey ? 600 : 0);
    return () => { live = false; window.clearTimeout(t); };
  }, [kind, worldId, range, order, limit, refreshKey]);
  return state;
}
